<script setup lang="ts">
import { mergeRegister } from "@lexical/utils";
import type { EditorState, RangeSelection, SerializedEditorState } from "lexical";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $parseSerializedNode,
  COMMAND_PRIORITY_LOW,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
} from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { nextTick, onMounted, onUnmounted, ref } from "vue";

import { isUserTextMessage } from "~/components/chat/messageSectionUtils";
import { useDataStore } from "~/stores";

const HISTORY_NAV_TAG = "chat-history-navigation";

const props = defineProps<{
  chatDuid: string;
}>();

const emit = defineEmits<{
  saveUnthrottled: [value: SerializedEditorState];
}>();

const editor = useLexicalComposer();
const dataStore = useDataStore();

const currentMessageIndex = ref(-1);
const draftSnapshot = ref<SerializedEditorState | null>(null);

const storeDraftSnapshotFromState = (state: EditorState) => {
  draftSnapshot.value = state.toJSON();
};

const getUserMessages = () => dataStore.getTextMessages(props.chatDuid).filter(isUserTextMessage);

const isSelectionAtDocumentStart = (selection: RangeSelection) => {
  if (!selection.isCollapsed() || selection.anchor.offset !== 0) {
    return false;
  }

  let node = selection.anchor.getNode();
  while (node) {
    if (node.getPreviousSibling()) {
      return false;
    }

    const parent = node.getParent();
    if (!parent) {
      return true;
    }

    if (parent.getType() === "root") {
      return true;
    }

    node = parent;
  }

  return true;
};

const focusEditor = async () => {
  await nextTick();
  editor.focus();
};

const loadMessageAtIndex = (index: number) => {
  const userMessages = getUserMessages();
  const message = userMessages[index];
  if (!message) {
    return;
  }

  editor.update(
    () => {
      const root = $getRoot();
      root.clear();

      message.content.root.children.forEach((child: unknown) => {
        const parsedNode = $parseSerializedNode(child as never);
        root.append(parsedNode);
      });

      const lastDescendant = root.getLastDescendant();
      if (lastDescendant) {
        lastDescendant.selectEnd();
      } else {
        root.selectEnd();
      }
      currentMessageIndex.value = index;
    },
    { tag: HISTORY_NAV_TAG }
  );
};

const restoreDraft = () => {
  if (!draftSnapshot.value) {
    editor.update(
      () => {
        $getRoot().clear();
        $getRoot().selectEnd();
      },
      { tag: HISTORY_NAV_TAG }
    );
    currentMessageIndex.value = -1;
    focusEditor();
    return;
  }

  const parsedState = editor.parseEditorState(draftSnapshot.value);
  editor.setEditorState(parsedState, { tag: HISTORY_NAV_TAG });
  currentMessageIndex.value = -1;
  focusEditor();
};

const handleArrowUp = () => {
  // we're already navigating the history
  if (currentMessageIndex.value >= 0) {
    if (currentMessageIndex.value === 0) {
      return true;
    }

    loadMessageAtIndex(currentMessageIndex.value - 1);
    return true;
  }

  const userMessages = getUserMessages();
  if (userMessages.length === 0) {
    return false;
  }

  let shouldLoadHistory = false;
  const editorState = editor.getEditorState();

  editorState.read(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection) && isSelectionAtDocumentStart(selection)) {
      shouldLoadHistory = true;
    }
  });

  if (!shouldLoadHistory) {
    return false;
  }

  storeDraftSnapshotFromState(editorState);
  loadMessageAtIndex(userMessages.length - 1);
  return true;
};

const handleArrowDown = () => {
  if (currentMessageIndex.value === -1) {
    return false;
  }

  const userMessages = getUserMessages();
  if (userMessages.length === 0) {
    restoreDraft();
    return true;
  }

  const nextIndex = currentMessageIndex.value + 1;
  if (nextIndex >= userMessages.length) {
    restoreDraft();
    return true;
  }

  loadMessageAtIndex(nextIndex);
  return true;
};

const handleArrowUpOrDown = (event: KeyboardEvent, isArrowUp: boolean) => {
  const result = isArrowUp ? handleArrowUp() : handleArrowDown();

  if (result) {
    event?.preventDefault();
    event?.stopPropagation();
  }
  return result;
};

const resetDraftSnapshot = () => {
  draftSnapshot.value = null;
  currentMessageIndex.value = -1;
};

let unregisterListeners: () => void;

onMounted(() => {
  storeDraftSnapshotFromState(editor.getEditorState());
  unregisterListeners = mergeRegister(
    editor.registerCommand(
      KEY_ARROW_UP_COMMAND,
      (event: KeyboardEvent) => handleArrowUpOrDown(event, true),
      COMMAND_PRIORITY_LOW
    ),
    editor.registerCommand(
      KEY_ARROW_DOWN_COMMAND,
      (event: KeyboardEvent) => handleArrowUpOrDown(event, false),
      COMMAND_PRIORITY_LOW
    ),
    editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves, tags }) => {
      if (tags.has(HISTORY_NAV_TAG)) {
        return;
      }

      if (currentMessageIndex.value >= 0) {
        const hasContentChanges = dirtyElements.size > 0 || dirtyLeaves.size > 0;
        if (!hasContentChanges) {
          return;
        }

        currentMessageIndex.value = -1;
        storeDraftSnapshotFromState(editorState);
        return;
      }

      storeDraftSnapshotFromState(editorState);
    })
  );
});

onUnmounted(() => {
  unregisterListeners?.();

  if (!draftSnapshot.value) {
    return;
  }
  emit("saveUnthrottled", draftSnapshot.value);
});

defineExpose({
  resetDraftSnapshot,
});
</script>

<template>
  <slot />
</template>
